/**
 * useTrialOnboardingGate — live wiring for the trial onboarding gate.
 *
 * Watches the signed-in user's subscription doc, onboarding session doc, and
 * org-membership back-pointer, and derives via the pure module
 * (trialOnboardingGate.ts) whether the user still owes onboarding — either
 * the trial staged flow (subscription exists, flow unfinished) or, since
 * task #56, account activation (no subscription, no org membership, not
 * internal/admin: the code-less brand-new signup). `loading` stays true
 * until EVERY signal has arrived (or auth resolves signed-out) so the router
 * never flash-redirects on a half-hydrated state.
 *
 * Fail-open by contract: a READ error on any doc must never lock a user out
 * of the app — "couldn't read" is tracked separately from "doc missing" so
 * the activation gate can tell a genuine new signup from a rules hiccup.
 *
 * INCIDENT 2026-08-14 (owner report): the first cut gated Fernando's
 * activated account and bounce-looped the mobile home page. Hardened since:
 * internal/admin accounts are exempt from the WHOLE gate (not just
 * activation), the activation test also consults the UID-keyed owner doc
 * (accounts/{uid}/profile/main — owners have no membership back-pointer)
 * and the auth account's creation time (pre-epoch accounts are never
 * activation-gated), and '/' is no longer redirected at all.
 */

import { useEffect, useState } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, doc, onSnapshot } from 'firebase/firestore';
import {
  GateSubscriptionShape,
  OnboardingSessionLike,
  needsAccountActivation,
  needsTrialOnboarding,
} from './trialOnboardingGate';
import { pickBackPointerAccountId } from '../services/prest0nRuntime/backPointer';
import { ACCOUNT_FIRESTORE_PREFIX } from '../services/prest0nRuntime/constants';
import { isInternalAccount } from '../config/internalAccess';
import { getPendingInvite } from '../lib/pendingInvite';
import { getJustJoinedOrg } from '../lib/justJoinedOrg';

export interface TrialOnboardingGateState {
  loading: boolean;
  needsOnboarding: boolean;
}

export function useTrialOnboardingGate(): TrialOnboardingGateState {
  const [state, setState] = useState<TrialOnboardingGateState>({
    loading: true,
    needsOnboarding: false,
  });

  useEffect(() => {
    let subLoaded = false;
    let sessionLoaded = false;
    let membershipLoaded = false;
    let ownerDocLoaded = false;
    let claimsLoaded = false;
    let sub: GateSubscriptionShape | null = null;
    let subReadFailed = false;
    let session: OnboardingSessionLike | null = null;
    let membershipAccountId: string | null = null;
    let membershipReadFailed = false;
    let ownerAccountDocExists = false;
    let internalOrAdmin = false;
    let accountCreatedAtMs: number | null = null;
    let currentUid = '';
    let unsubs: Array<() => void> = [];
    // Guards a slow getIdTokenResult from a PREVIOUS user landing after an
    // account switch.
    let authGeneration = 0;

    const recompute = () => {
      const loading = !(subLoaded && sessionLoaded && membershipLoaded && ownerDocLoaded && claimsLoaded);
      // The invite markers are cheap sync localStorage reads — evaluated at
      // every snapshot event, and both invite paths trigger doc writes that
      // re-fire this anyway.
      const hasPendingInvite = Boolean(getPendingInvite() || getJustJoinedOrg());
      setState({
        loading,
        // Internal/admin accounts are NEVER gated — by either test
        // (2026-08-14 incident hardening).
        needsOnboarding: !loading && !internalOrAdmin && (
          needsTrialOnboarding(sub, session, Date.now(), {
            uid: currentUid,
            activeMembershipAccountId: membershipAccountId,
            hasPendingInvite,
          })
          || needsAccountActivation({
            subscription: sub,
            subscriptionReadFailed: subReadFailed,
            membershipAccountId,
            membershipReadFailed,
            ownerAccountDocExists,
            session,
            isInternalOrAdmin: internalOrAdmin,
            hasPendingInvite,
            accountCreatedAtMs,
          })
        ),
      });
    };

    const detachDocs = () => {
      unsubs.forEach((u) => u());
      unsubs = [];
    };

    const unsubAuth = onAuthStateChanged(getAuth(), (user) => {
      authGeneration += 1;
      const gen = authGeneration;
      detachDocs();
      currentUid = user?.uid ?? '';
      if (!user) {
        subLoaded = true;
        sessionLoaded = true;
        membershipLoaded = true;
        ownerDocLoaded = true;
        claimsLoaded = true;
        sub = null;
        subReadFailed = false;
        session = null;
        membershipAccountId = null;
        membershipReadFailed = false;
        ownerAccountDocExists = false;
        internalOrAdmin = false;
        accountCreatedAtMs = null;
        recompute();
        return;
      }
      subLoaded = false;
      sessionLoaded = false;
      membershipLoaded = false;
      ownerDocLoaded = false;
      claimsLoaded = false;
      subReadFailed = false;
      membershipReadFailed = false;
      ownerAccountDocExists = false;
      // Auth account age — pre-epoch accounts are never activation-gated.
      // Unknown/missing metadata parses to NaN → stored as null → stand down.
      const createdAtRaw = user.metadata?.creationTime;
      const createdAtParsed = createdAtRaw ? Date.parse(createdAtRaw) : NaN;
      accountCreatedAtMs = Number.isFinite(createdAtParsed) ? createdAtParsed : null;
      // Internal allowlist resolves synchronously off the auth user…
      internalOrAdmin = isInternalAccount({ uid: user.uid, email: user.email });
      // …and the admin custom claim tops it up from the (cached) ID token.
      user.getIdTokenResult()
        .then((r) => {
          if (gen !== authGeneration) return;
          const claims = (r?.claims ?? {}) as Record<string, unknown>;
          internalOrAdmin = internalOrAdmin || claims.admin === true || claims.role === 'admin';
          claimsLoaded = true;
          recompute();
        })
        .catch(() => {
          // Fail-open only matters for admin-claim-ONLY accounts; the
          // allowlist above already covered internal staff synchronously.
          if (gen !== authGeneration) return;
          claimsLoaded = true;
          recompute();
        });
      const db = getFirestore();
      unsubs.push(onSnapshot(
        doc(db, 'users', user.uid, 'subscription', 'subscription'),
        (snap) => {
          sub = snap.exists() ? (snap.data() as GateSubscriptionShape) : null;
          subReadFailed = false;
          subLoaded = true;
          recompute();
        },
        () => {
          // Fail-open: a read error must never lock the user out of the app.
          sub = null;
          subReadFailed = true;
          subLoaded = true;
          recompute();
        },
      ));
      unsubs.push(onSnapshot(
        doc(db, 'users', user.uid, 'onboarding', 'session'),
        (snap) => {
          session = snap.exists() ? (snap.data() as OnboardingSessionLike) : null;
          sessionLoaded = true;
          recompute();
        },
        () => {
          // Fail-open on session read errors too (missing rules, offline):
          // treat as complete rather than trapping an unknown state.
          session = { state: 'complete' };
          sessionLoaded = true;
          recompute();
        },
      ));
      // Org back-pointer: the ONLY doc an invited member is guaranteed to
      // have (acceptance never writes a subscription doc) — self-read is
      // always allowed by rules, no index needed.
      unsubs.push(onSnapshot(
        doc(db, 'users', user.uid, 'account_membership', 'main'),
        (snap) => {
          membershipAccountId = snap.exists()
            ? pickBackPointerAccountId(snap.data() as Record<string, unknown>)
            : null;
          membershipReadFailed = false;
          membershipLoaded = true;
          recompute();
        },
        () => {
          // Fail-open: an unreadable back-pointer must read as "member".
          membershipAccountId = null;
          membershipReadFailed = true;
          membershipLoaded = true;
          recompute();
        },
      ));
      // UID-keyed owner account (2026-08-14 incident): account OWNERS often
      // have NO back-pointer — the resolver finds them via
      // accounts/{uid}/profile/main directly. Anyone with that doc has a
      // workspace and must never be treated as an unactivated signup.
      unsubs.push(onSnapshot(
        doc(db, ACCOUNT_FIRESTORE_PREFIX, user.uid, 'profile', 'main'),
        (snap) => {
          ownerAccountDocExists = snap.exists();
          ownerDocLoaded = true;
          recompute();
        },
        () => {
          // Fail-open: an unreadable owner doc must read as "owner".
          ownerAccountDocExists = true;
          ownerDocLoaded = true;
          recompute();
        },
      ));
    });

    return () => {
      unsubAuth();
      detachDocs();
    };
  }, []);

  return state;
}
